import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Checkbox,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Upload as UploadIcon,
  PhotoCamera as PhotoIcon,
  Description as DocumentIcon,
  Assignment as PassportIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FlightTakeoff as DepartureIcon,
  FlightLand as ArrivalIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AdminLayout from '../components/layout/AdminLayout';
import DocumentUpload from '../components/DocumentUpload';
import { ResponsiveContainer, ResponsiveCard, MobileDataTable } from '../components/ui';
import { travelersAPI, toursAPI, documentsAPI, pdfDocumentsAPI, apiCall } from '../services/api';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

// Persistent state management using sessionStorage
const usePersistedState = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading ${key} from sessionStorage:`, error);
      return defaultValue;
    }
  });

  const setPersistedState = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(state) : value;
      setState(valueToStore);
      sessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error saving ${key} to sessionStorage:`, error);
      setState(value instanceof Function ? value(state) : value);
    }
  }, [key, state]);

  return [state, setPersistedState];
};

const TravelersManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Persistent state that survives page navigation
  const [selectedTourId, setSelectedTourId] = usePersistedState('travelers_selectedTourId', '');
  const [searchTerm, setSearchTerm] = usePersistedState('travelers_searchTerm', '');
  const [filters, setFilters] = usePersistedState('travelers_filters', {
    gender: '',
    group_number: '',
    passport_expiring: false,
    missing_documents: false,
    incomplete_data: false,
  });
  const [currentPage, setCurrentPage] = usePersistedState('travelers_currentPage', 0);
  
  // Non-persistent state
  const [travelers, setTravelers] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toursLoading, setToursLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  
  // Table sorting and filtering
  const [sortConfig, setSortConfig] = usePersistedState('travelers_sortConfig', {
    key: null,
    direction: 'asc'
  });
  const [columnFilters, setColumnFilters] = usePersistedState('travelers_columnFilters', {});
  const [flightFilters, setFlightFilters] = usePersistedState('travelers_flightFilters', {
    departureDate: '',
    returnDate: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    male: 0,
    female: 0,
    passportExpiring: 0,
    under12: 0,
    withAllDocuments: 0,
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 50,
    total: 0,
  });

  // Dialog states
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedTraveler, setSelectedTraveler] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [currentCsvFile, setCurrentCsvFile] = useState(null);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvProgress, setCsvProgress] = useState({ current: 0, total: 0, currentBatch: 0, totalBatches: 0 });
  const [csvTourId, setCsvTourId] = useState('');
  const [columnConfigDialog, setColumnConfigDialog] = useState(false);
  const [pdfDownloadDialog, setPdfDownloadDialog] = useState(false);
  const [pdfFlights, setPdfFlights] = useState([]);
  const [pdfReturnFlights, setPdfReturnFlights] = useState([]);
  const [pdfFlightType, setPdfFlightType] = useState('departure'); // 'departure' or 'return'
  const [selectedPdfFlight, setSelectedPdfFlight] = useState('');
  const [pdfDocumentType, setPdfDocumentType] = useState('visa');
  const [compileSinglePdf, setCompileSinglePdf] = useState(false);
  const [watermarkFontSize, setWatermarkFontSize] = useState(42);
  const [regeneratingQR, setRegeneratingQR] = useState(false);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = usePersistedState('travelers_visibleColumns', {
    traveler: true,
    tour: true,
    idCard: true,
    hajjiNo: false,
    type: false,
    mhcNo: false,
    passport: true,
    contact: false,
    address: false,
    departureFlights: false,
    returnFlights: false,
    busNumber: false,
    documents: true,
    group: true,
    medical: false,
    status: true,
    actions: true
  });

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    tour_id: '',
    hajji_no: '',
    type: '',
    mhc_no: '',
    id_card: '',
    dhivehi_name: '',
    arabic_name: '',
    date_of_birth: '',
    age: '',
    gender: 'Male',
    mahram: '',
    permanent_address_dhivehi: '',
    permanent_atoll: '',
    permanent_island: '',
    current_address_dhivehi: '',
    current_atoll: '',
    current_island: '',
    phone: '',
    sms_numbers: '',
    emergency_contact: '',
    saudi_phone: '',
    passport_number: '',
    passport_issued: '',
    passport_expiry: '',
    care_of: '',
    departure_flight: '',
    return_flight: '',
    bus: '',
    group_number: '',
    makkah_room: '',
    madina_room: ''
  });

  // Use ref to avoid re-renders affecting search input
  const searchInputRef = useRef(null);
  
  // Debounce search term for API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Memoized filter object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => ({
    ...filters,
    search: debouncedSearchTerm,
    tour_id: selectedTourId,
    departure_flight: flightFilters.departureDate,
    return_flight: flightFilters.returnDate
  }), [filters, debouncedSearchTerm, selectedTourId, flightFilters]);

  // Load tours on component mount
  const fetchTours = useCallback(async () => {
    try {
      setToursLoading(true);
      const toursData = await apiCall(toursAPI.getAll);
      const toursList = toursData.tours || [];
      setTours(toursList);
      
      // Validate that the selected tour still exists
      if (selectedTourId && !toursList.find(t => t.id.toString() === selectedTourId.toString())) {
        console.warn(`Selected tour ID ${selectedTourId} not found in available tours, resetting to empty`);
        setSelectedTourId('');
      }
      
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setToursLoading(false);
    }
  }, [selectedTourId, setSelectedTourId]);

  // Load statistics from server
  const fetchStatistics = useCallback(async () => {
    if (!selectedTourId) {
      // Reset stats when no tour is selected
      setStats({
        total: 0,
        male: 0,
        female: 0,
        passportExpiring: 0,
        under12: 0,
        withAllDocuments: 0,
      });
      return;
    }

    try {
      const response = await apiCall(travelersAPI.getTourStatistics, selectedTourId);
      if (response.statistics) {
        setStats({
          total: response.statistics.total,
          male: response.statistics.male,
          female: response.statistics.female,
          passportExpiring: response.statistics.passportExpiring + response.statistics.passportExpired,
          under12: response.statistics.under12,
          withAllDocuments: response.statistics.withAllDocuments,
        });
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
      // Keep existing stats on error to avoid breaking the UI
    }
  }, [selectedTourId]);

  // Load travelers data
  const fetchTravelers = useCallback(async (resetPage = false) => {
    // Don't fetch if no tour is selected
    if (!selectedTourId) {
      setTravelers([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }

    try {
      setLoading(true);
      
      const currentPageToUse = resetPage ? 0 : currentPage;
      
      // Build request parameters
      const travelerParams = {
        tour_id: selectedTourId,
        limit: pagination.limit,
        offset: currentPageToUse * pagination.limit,
      };
      
      // Add filters to request
      if (memoizedFilters.search) travelerParams.search = memoizedFilters.search;
      if (memoizedFilters.gender) travelerParams.gender = memoizedFilters.gender;
      if (memoizedFilters.group_number) travelerParams.group_number = memoizedFilters.group_number;
      if (memoizedFilters.passport_expiring) travelerParams.passport_expiring = true;
      if (memoizedFilters.missing_documents) travelerParams.missing_documents = true;
      if (memoizedFilters.incomplete_data) travelerParams.incomplete_data = true;
      if (memoizedFilters.departure_flight) travelerParams.departure_flight = memoizedFilters.departure_flight;
      if (memoizedFilters.return_flight) travelerParams.return_flight = memoizedFilters.return_flight;

      const travelersData = await apiCall(travelersAPI.getAll, travelerParams);

      // Ensure unique travelers by ID to prevent rendering duplicates
      const uniqueTravelers = travelersData.travelers.reduce((acc, traveler) => {
        if (!acc.find(t => t.id === traveler.id)) {
          acc.push(traveler);
        }
        return acc;
      }, []);

      setTravelers(uniqueTravelers);
      
      // Extract total count from pagination object
      const totalCount = travelersData.pagination?.total || travelersData.totalCount || travelersData.travelers?.length || 0;
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        page: currentPageToUse,
        total: totalCount
      }));
      
      if (resetPage) {
        setCurrentPage(0);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTourId, memoizedFilters, pagination.limit, currentPage]);

  // Effects
  useEffect(() => {
    document.title = 'Travelers - PIMS';
    fetchTours();
    fetchStatistics(); // Load statistics on mount
  }, [fetchTours, fetchStatistics]);

  // Fetch travelers when tour, filters, or page changes
  useEffect(() => {
    fetchTravelers();
  }, [selectedTourId, memoizedFilters, currentPage]);

  // Fetch statistics when selected tour changes
  useEffect(() => {
    fetchStatistics();
  }, [selectedTourId, fetchStatistics]);

  // Get unique flight dates for filtering - fetch from server for complete data
  const [availableFlightDates, setAvailableFlightDates] = useState({
    departureDates: [],
    returnDates: []
  });

  // Fetch available flight dates for the selected tour
  const fetchFlightDates = useCallback(async () => {
    if (!selectedTourId) {
      setAvailableFlightDates({ departureDates: [], returnDates: [] });
      return;
    }
    
    try {
      // Fetch all travelers for this tour to get unique flight dates
      const response = await apiCall(travelersAPI.getAll, { 
        tour_id: selectedTourId, 
        limit: 10000 // Get all travelers to extract flight dates
      });
      
      const departureDates = [...new Set(response.travelers.map(t => t.departure_flight).filter(Boolean))].sort();
      const returnDates = [...new Set(response.travelers.map(t => t.return_flight).filter(Boolean))].sort();
      
      setAvailableFlightDates({ departureDates, returnDates });
    } catch (err) {
      console.error('Error fetching flight dates:', err);
      setAvailableFlightDates({ departureDates: [], returnDates: [] });
    }
  }, [selectedTourId]);

  // Fetch flight dates when tour changes
  useEffect(() => {
    fetchFlightDates();
  }, [fetchFlightDates]);

  // Event handlers
  const handleTourChange = (tourId) => {
    setSelectedTourId(tourId);
    setCurrentPage(0);
    // Reset flight filters when changing tours
    setFlightFilters({ departureDate: '', returnDate: '' });
    if (!tourId) {
      setTravelers([]);
      // Statistics will be refreshed from server automatically
    }
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentPage(0); // Reset to first page when searching
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(0); // Reset to first page when filtering
  };

  const handleFlightFilterChange = (filterName, value) => {
    setFlightFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleMenuClick = (event, traveler) => {
    // Validate traveler data before setting
    if (!traveler || !traveler.id) {
      console.error('Cannot select traveler: Invalid traveler data', traveler);
      setError('Cannot select traveler: Invalid traveler data');
      return;
    }
    setAnchorEl(event.currentTarget);
    setSelectedTraveler(traveler);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedTraveler here as it's needed for edit dialog
  };

  // Sorting and filtering functions
  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearColumnFilter = (column) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
  };

  // Remove client-side filtering - all filtering now happens server-side
  // This was causing the issue where only the current page was being filtered
  const getSortedAndFilteredTravelers = () => {
    // Just return travelers as-is since all filtering is now server-side
    return travelers;
  };

  const getColumnValue = (traveler, column) => {
    switch (column) {
      case 'name': return traveler.name;
      case 'hajji_no': return traveler.hajji_no;
      case 'id_card': return traveler.id_card;
      case 'type': return traveler.type;
      case 'mhc_no': return traveler.mhc_no;
      case 'passport_no': return traveler.passport_no;
      case 'phone': return traveler.phone;
      case 'emergency_contact': return traveler.emergency_contact;
      case 'departure_flight': return traveler.departure_flight;
      case 'return_flight': return traveler.return_flight;
      case 'group_number': return traveler.group_number;
      case 'bus': return traveler.bus;
      case 'permanent_atoll': return traveler.permanent_atoll;
      case 'permanent_island': return traveler.permanent_island;
      case 'makkah_room': return traveler.makkah_room;
      case 'madina_room': return traveler.madina_room;
      case 'gender': return traveler.gender;
      case 'medical': return traveler.medical;
      case 'agreement': return traveler.agreement;
      case 'age': return traveler.age;
      default: return '';
    }
  };

  // Simple sortable header component
  const SortableTableCell = ({ children, sortKey }) => {
    const getSortIcon = () => {
      if (sortConfig.key !== sortKey) return null;
      return sortConfig.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />;
    };

    return (
      <TableCell sx={{ fontWeight: 600 }}>
        <Box 
          display="flex" 
          alignItems="center" 
          gap={0.5}
          sx={{ cursor: sortKey ? 'pointer' : 'default' }}
          onClick={() => sortKey && handleSort(sortKey)}
        >
          {children}
          {getSortIcon()}
        </Box>
      </TableCell>
    );
  };

  const handleEdit = (traveler) => {
    // Validate traveler exists and has valid ID
    if (!traveler || !traveler.id) {
      console.error('Cannot edit traveler: Invalid traveler data', traveler);
      setError('Cannot edit traveler: Invalid traveler data');
      return;
    }

    setFormData({
      id: traveler.id,
      name: traveler.name || '',
      tour_id: traveler.tour_id || '',
      hajji_no: traveler.hajji_no || '',
      type: traveler.type || '',
      mhc_no: traveler.mhc_no || '',
      id_card: traveler.id_card || '',
      dhivehi_name: traveler.dhivehi_name || '',
      arabic_name: traveler.arabic_name || '',
      date_of_birth: traveler.dob ? traveler.dob.split('T')[0] : '',
      age: traveler.age || '',
      gender: traveler.gender || 'Male',
      mahram: traveler.mahram || '',
      permanent_address_dhivehi: traveler.permanent_address_dhivehi || '',
      permanent_atoll: traveler.permanent_atoll || '',
      permanent_island: traveler.permanent_island || '',
      current_address_dhivehi: traveler.current_address_dhivehi || '',
      current_atoll: traveler.current_atoll || '',
      current_island: traveler.current_island || '',
      phone: traveler.phone || '',
      sms_numbers: traveler.sms_numbers || '',
      emergency_contact: traveler.emergency_contact || '',
      saudi_phone: traveler.saudi_phone || '',
      passport_number: traveler.passport_no || '',
      passport_issued: traveler.passport_issued ? traveler.passport_issued.split('T')[0] : '',
      passport_expiry: traveler.passport_expiry ? traveler.passport_expiry.split('T')[0] : '',
      care_of: traveler.care_of || '',
      departure_flight: traveler.departure_flight || '',
      return_flight: traveler.return_flight || '',
      bus: traveler.bus || '',
      group_number: traveler.group_number || '',
      medical: traveler.medical || 'No',
      agreement: traveler.agreement || 'No',
      makkah_room: traveler.makkah_room || '',
      madina_room: traveler.madina_room || ''
    });
    setSelectedTraveler(traveler); // Set the selected traveler for the update button
    console.log('🟢 Edit dialog opened for traveler:', traveler.name, 'ID:', traveler.id);
    console.log('🟢 Selected traveler set to:', traveler);
    setEditDialog(true);
    handleMenuClose();
  };

  const handleDelete = async (traveler) => {
    if (window.confirm(`Are you sure you want to delete traveler "${traveler.name}"?`)) {
      try {
        await apiCall(travelersAPI.delete, traveler.id);
        fetchTravelers();
        fetchStatistics(); // Refresh statistics after deletion
      } catch (err) {
        setError(err.message);
      }
    }
    handleMenuClose();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddTraveler = async (e) => {
    e.preventDefault();
    try {
      const backendData = {
        name: formData.name,
        tour_id: parseInt(formData.tour_id),
        hajji_no: formData.hajji_no ? parseInt(formData.hajji_no) : null,
        group_number: formData.group_number ? parseInt(formData.group_number) : null,
        gender: formData.gender,
        dob: formData.date_of_birth || null,
        passport_no: formData.passport_number,
        passport_expiry: formData.passport_expiry,
        permanent_island: formData.nationality || null
      };

      await apiCall(travelersAPI.create, backendData);
      setAddDialog(false);
      setFormData({
        name: '',
        tour_id: '',
        hajji_no: '',
        group_number: '',
        gender: 'Male',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: '',
        nationality: ''
      });
      fetchTravelers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateTraveler = async (e) => {
    e.preventDefault();
    
    console.log('🔄 Update traveler form submitted!');
    console.log('🔍 Selected traveler:', selectedTraveler);
    console.log('🔍 Form data:', formData);
    
    // Add null check to prevent errors
    if (!selectedTraveler?.id) {
      console.warn('❌ Update attempted without selected traveler');
      setError('No traveler selected for update');
      return;
    }
    
    try {
      const backendData = {
        name: formData.name,
        dhivehi_name: formData.dhivehi_name || null,
        arabic_name: formData.arabic_name || null,
        type: formData.type || '',
        mhc_no: formData.mhc_no || null,
        id_card: formData.id_card || null,
        hajji_no: formData.hajji_no && formData.hajji_no.toString().trim() !== '' ? formData.hajji_no.toString().trim() : null,
        group_number: formData.group_number && formData.group_number.toString().trim() !== '' ? parseInt(formData.group_number) : null,
        gender: formData.gender,
        dob: formData.date_of_birth || null,
        age: formData.age && formData.age.toString().trim() !== '' ? parseInt(formData.age) : null,
        mahram: formData.mahram || null,
        permanent_address_dhivehi: formData.permanent_address_dhivehi || null,
        permanent_atoll: formData.permanent_atoll || null,
        permanent_island: formData.permanent_island || null,
        current_address_dhivehi: formData.current_address_dhivehi || null,
        current_atoll: formData.current_atoll || null,
        current_island: formData.current_island || null,
        phone: formData.phone || null,
        sms_numbers: formData.sms_numbers || null,
        emergency_contact: formData.emergency_contact || null,
        saudi_phone: formData.saudi_phone || null,
        passport_no: formData.passport_number || null,
        passport_issued: formData.passport_issued || null,
        passport_expiry: formData.passport_expiry || null,
        departure_flight: formData.departure_flight || null,
        return_flight: formData.return_flight || null,
        bus: formData.bus ? parseInt(formData.bus) : null,
        care_of: formData.care_of || null,
        medical: formData.medical,
        agreement: formData.agreement,
        makkah_room: formData.makkah_room || null,
        madina_room: formData.madina_room || null
      };

      await apiCall(travelersAPI.update, selectedTraveler.id, backendData);
      setEditDialog(false);
      setSelectedTraveler(null);
      fetchTravelers();
      fetchStatistics(); // Refresh statistics after update
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadQR = async (traveler) => {
    try {
      const qrData = await apiCall(travelersAPI.generateQRCode, traveler.id);
      
      // Create a link element to download the pre-generated QR code file
      const link = document.createElement('a');
      link.href = qrData.qr_code.image;
      link.download = `${traveler.id_card}.png`;
      link.target = '_blank';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      setError(err.message);
    }
    handleMenuClose();
  };

  const handleViewProfile = async (traveler) => {
    try {
      const qrData = await apiCall(travelersAPI.generateQRCode, traveler.id);
      window.open(qrData.qr_code.url, '_blank');
    } catch (err) {
      setError(err.message);
    }
    handleMenuClose();
  };

  const handleGenerateAllQR = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/qr-codes/generate-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ tour_id: selectedTourId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate QR codes');
      }
      
      const result = await response.json();
      alert(`QR Code Generation Complete!\nTotal: ${result.total}\nGenerated: ${result.generated}\nExisting: ${result.existing}\nErrors: ${result.errors}`);
      
    } catch (error) {
      console.error('Error generating QR codes:', error);
      setError('Failed to generate QR codes');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkQRDownload = async () => {
    try {
      const tourData = tours.find(t => t.id === parseInt(selectedTourId));
      if (!tourData) return;

      setLoading(true);
      const response = await fetch(`/api/qr-codes/bulk-download?tour_id=${selectedTourId}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to download QR codes');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tourData.name.replace(/[^a-zA-Z0-9]/g, '_')}_QR_Codes.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading QR codes:', error);
      setError('Failed to download QR codes');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateQRCodes = async () => {
    if (!selectedTourId) {
      setError('Please select a tour first');
      return;
    }

    const tourData = tours.find(t => t.id === parseInt(selectedTourId));
    if (!tourData) return;

    const confirmed = window.confirm(
      `Regenerate QR codes for "${tourData.name}"?\n\nThis will regenerate QR codes for all travelers in this tour using the current QR design settings.\n\nThe regeneration will run in the background on the server.`
    );

    if (!confirmed) return;

    try {
      setRegeneratingQR(true);
      setError(null);

      const response = await fetch(`/api/qr-codes/regenerate-tour/${selectedTourId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      // Handle both 202 (background job started) and error responses
      if (response.status === 202) {
        // Background job started successfully - use INFO message, not success
        const message = `⏳ QR regeneration STARTED for ${tourData.name} (${data.totalTravelers} travelers).\n\nThe server is processing QR codes in the background. This will take a few minutes. Check the server logs to monitor progress.`;
        alert(message); // Use alert to make it clear this is just the start

        // Also set a subtle success message
        setSuccess(`QR regeneration job started for ${data.totalTravelers} travelers. Processing in background...`);

        // Clear after 15 seconds
        setTimeout(() => setSuccess(''), 15000);

      } else if (!response.ok) {
        throw new Error(data.error || 'Failed to start QR code regeneration');
      }

    } catch (error) {
      console.error('Error regenerating QR codes:', error);
      setError(error.message || 'Failed to start QR code regeneration');
    } finally {
      setRegeneratingQR(false);
    }
  };

  // CSV handling
  const handleCsvFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Store the file for bulk import
    setCurrentCsvFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let csvText = e.target.result;
        
        // Remove BOM if present
        if (csvText.charCodeAt(0) === 0xFEFF) {
          csvText = csvText.slice(1);
        }
        
        const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
        
        // Find the header row - look for a line that contains common CSV headers
        let headerRowIndex = -1;
        let headers = [];
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          // Look for a row that has the expected structure (starts with # or has Name, Passport, etc.)
          if (line.includes('"Name"') || line.includes('Name,') || line.includes('#,Type')) {
            headerRowIndex = i;
            // Parse CSV headers properly (handling quotes and complex structure)
            headers = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                headers.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            if (current.trim()) {
              headers.push(current.trim());
            }
            break;
          }
        }
        
        if (headerRowIndex === -1) {
          throw new Error('Could not find valid CSV headers. Please ensure the file has proper headers.');
        }
        
        console.log('Found headers at row', headerRowIndex + 1, ':', headers);
        
        // Parse data rows starting after the header
        const dataRows = lines.slice(headerRowIndex + 1);
        const data = dataRows
          .filter(line => line.trim() && !line.startsWith('#') && line.includes(','))
          .map((line, index) => {
            try {
              // Parse CSV row properly (handling quotes and complex structure)
              const cleanValues = [];
              let current = '';
              let inQuotes = false;
              
              for (let j = 0; j < line.length; j++) {
                const char = line[j];
                if (char === '"') {
                  inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                  cleanValues.push(current.trim());
                  current = '';
                } else {
                  current += char;
                }
              }
              if (current.trim() || cleanValues.length < headers.length) {
                cleanValues.push(current.trim());
              }
              
              const row = {};
              headers.forEach((header, headerIndex) => {
                row[header] = cleanValues[headerIndex] || '';
              });
              
              // Debug: log first row to see structure
              if (index === 0) {
                console.log('First row headers:', headers);
                console.log('First row values:', cleanValues);
                console.log('First row mapped:', row);
              }

              // Map common fields to expected names for display
              return {
                Name: row['Name'] || row['name'] || '',
                Passport: row['PP No'] || row['Passport No'] || row['passport_no'] || '',
                Gender: row['Gender'] || row['gender'] || '',
                Group: row['Group'] || row['group_number'] || '',
                Age: row['Age'] || row['age'] || '',
                Type: row['Type'] || row['type'] || '',
                'Hajji No': row['Hajji No'] || row['hajji_no'] || '',
                DOB: row['DOB'] || row['dob'] || '',
                Phone: row['Phone'] || row['phone'] || '',
                rawData: row // Keep original data for import
              };
            } catch (rowError) {
              console.warn(`Error parsing row ${index + 1}:`, rowError.message);
              return null;
            }
          })
          .filter(row => row !== null && row.Name); // Only include rows with names
        
        console.log(`Successfully parsed ${data.length} travelers from CSV`);
        setCsvData(data);
        setCsvDialog(true);
      } catch (error) {
        console.error('CSV parsing error:', error);
        setError(`Failed to parse CSV file: ${error.message}`);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
    event.target.value = '';
  };

  // Retry API call with exponential backoff for rate limiting errors
  const retryApiCall = async (apiCallFn, maxRetries = 5, baseDelay = 3000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall(apiCallFn);
      } catch (error) {
        const is429Error = error.message?.includes('429') || 
                           error.message?.includes('Too many requests') ||
                           error.message?.includes('Rate limit') ||
                           error.status === 429;
        
        if (is429Error && attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff: 3s, 6s, 12s, 24s
          console.log(`⚠️ Rate limit hit (attempt ${attempt}/${maxRetries}), waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        throw error; // Re-throw if not a 429 error or max retries reached
      }
    }
  };

  // Handle CSV import
// New bulk import function to replace the existing one
const handleImportTravelers = async () => {
  if (!csvTourId || !currentCsvFile) {
    setError('Please select a tour and ensure CSV file is loaded');
    return;
  }

  setCsvProcessing(true);
  setCsvProgress({ current: 0, total: 100, currentBatch: 1, totalBatches: 1 });
  setError('');

  try {
    console.log(`Starting bulk import of CSV file to tour ${csvTourId}`);
    
    // Update progress to show processing
    setCsvProgress({ current: 10, total: 100, currentBatch: 1, totalBatches: 1 });
    
    // Use the new bulk import API
    const response = await apiCall(travelersAPI.bulkImport, currentCsvFile, csvTourId);
    
    // Update progress to show completion
    setCsvProgress({ current: 100, total: 100, currentBatch: 1, totalBatches: 1 });
    
    console.log('✅ Bulk import completed:', response);
    
    // Process the results from backend
    const { results, summary } = response;
    
    let detailedMessage = `Import completed: ${summary.successful_imports} successful, ${summary.failed_imports} failed`;
    
    if (results.warnings && results.warnings.length > 0) {
      detailedMessage += `\n\n⚠️ Warnings (${results.warnings.length}):\n`;
      results.warnings.slice(0, 5).forEach(w => {
        detailedMessage += `• ${w.traveler} (#${w.hajji_no}): ${w.warnings.join(', ')}\n`;
      });
      if (results.warnings.length > 5) {
        detailedMessage += `... and ${results.warnings.length - 5} more warnings\n`;
      }
    }

    if (results.errors && results.errors.length > 0) {
      detailedMessage += `\n\n❌ Errors (${results.errors.length}):\n`;
      results.errors.slice(0, 5).forEach(e => {
        detailedMessage += `• ${e.traveler} (#${e.hajji_no}): ${e.error}\n`;
      });
      if (results.errors.length > 5) {
        detailedMessage += `... and ${results.errors.length - 5} more errors\n`;
      }
    }

    setSuccess(detailedMessage);
    setTimeout(() => setSuccess(''), 15000);
    
    await fetchTravelers();
    setCsvDialog(false);
    setCsvData([]);
    setCurrentCsvFile(null);
    
  } catch (err) {
    console.error('❌ Bulk import error:', err);
    setError('Failed to import travelers: ' + (err.message || 'Unknown error'));
  } finally {
    setCsvProcessing(false);
    setCsvProgress({ current: 0, total: 0, currentBatch: 0, totalBatches: 0 });
  }
};  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const getPassportStatusChip = (status, expiryDate) => {
    if (status === 'EXPIRED') {
      return <Chip label="Expired" color="error" size="small" icon={<WarningIcon />} />;
    } else if (status === 'EXPIRING_SOON') {
      return <Chip label="Expiring Soon" color="warning" size="small" icon={<WarningIcon />} />;
    } else {
      return <Chip label="Valid" color="success" size="small" />;
    }
  };

  const getDocumentChips = (traveler) => {
    const chips = [];
    
    // Profile Photo
    chips.push(
      <Chip
        key="photo"
        label="Photo"
        size="small"
        color={traveler.documents?.profile_photo ? "success" : "default"}
        icon={<PhotoIcon />}
        sx={{ 
          mr: 0.5, mb: 0.5,
          cursor: traveler.documents?.profile_photo ? 'pointer' : 'default',
          opacity: traveler.documents?.profile_photo ? 1 : 0.5
        }}
        onClick={() => {
          if (traveler.documents?.profile_photo) {
            handleViewDocument(traveler.documents.profile_photo, `${traveler.name} - Profile Photo`, traveler.uuid);
          }
        }}
      />
    );
    
    // Visa
    chips.push(
      <Chip
        key="visa"
        label="Visa"
        size="small"
        color={traveler.documents?.visa ? "success" : "default"}
        icon={<DocumentIcon />}
        sx={{ 
          mr: 0.5, mb: 0.5,
          cursor: traveler.documents?.visa ? 'pointer' : 'default',
          opacity: traveler.documents?.visa ? 1 : 0.5
        }}
        onClick={() => {
          if (traveler.documents?.visa) {
            handleViewDocument(traveler.documents.visa, `${traveler.name} - Visa`, traveler.uuid);
          }
        }}
      />
    );
    
    // Passport
    chips.push(
      <Chip
        key="passport"
        label="Passport"
        size="small"
        color={traveler.documents?.passport ? "success" : "default"}
        icon={<PassportIcon />}
        sx={{ 
          mr: 0.5, mb: 0.5,
          cursor: traveler.documents?.passport ? 'pointer' : 'default',
          opacity: traveler.documents?.passport ? 1 : 0.5
        }}
        onClick={() => {
          if (traveler.documents?.passport) {
            handleViewDocument(traveler.documents.passport, `${traveler.name} - Passport`, traveler.uuid);
          }
        }}
      />
    );
    
    return chips;
  };

  const [documentDialog, setDocumentDialog] = useState({ open: false, document: null, title: '', url: '', blobUrl: null, useIframe: false });
  
  // Bulk edit functionality
  const [selectedTravelers, setSelectedTravelers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkCompleteDialog, setBulkCompleteDialog] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    departure_flight: '',
    return_flight: '',
    makkah_room: '',
    madina_room: '',
    bus: '',
    group_number: ''
  });

  // Bulk edit handlers
  const handleSelectTraveler = (travelerId, isSelected) => {
    if (isSelected) {
      setSelectedTravelers(prev => [...prev, travelerId]);
    } else {
      setSelectedTravelers(prev => prev.filter(id => id !== travelerId));
    }
  };

  const handleSelectAll = (isSelected) => {
    setSelectAll(isSelected);
    if (isSelected) {
      setSelectedTravelers(travelers.map(t => t.id));
    } else {
      setSelectedTravelers([]);
    }
  };

  const handleBulkEdit = () => {
    if (selectedTravelers.length === 0) {
      alert('Please select travelers to edit');
      return;
    }
    setBulkEditDialog(true);
  };

  const handleBulkEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Filter out empty values
      const updateData = Object.fromEntries(
        Object.entries(bulkEditData).filter(([key, value]) => value !== '')
      );
      
      if (Object.keys(updateData).length === 0) {
        alert('Please enter at least one value to update');
        return;
      }

      const response = await apiCall(() => 
        fetch('/api/travelers/bulk-update', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            traveler_ids: selectedTravelers,
            update_data: updateData
          })
        })
      );

      if (response.ok) {
        alert(`Successfully updated ${selectedTravelers.length} travelers`);
        setBulkEditDialog(false);
        setSelectedTravelers([]);
        setSelectAll(false);
        fetchStatistics(); // Refresh statistics after bulk update
        setBulkEditData({
          departure_flight: '',
          return_flight: '',
          makkah_room: '',
          madina_room: '',
          bus: '',
          group_number: ''
        });
        fetchTravelers(); // Refresh the list
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkComplete = () => {
    if (selectedTravelers.length === 0) {
      alert('Please select travelers to mark as complete');
      return;
    }
    setBulkCompleteDialog(true);
  };

  const handleBulkCompleteSubmit = async () => {
    try {
      setLoading(true);
      
      const response = await apiCall(() => travelersAPI.bulkMarkComplete(selectedTravelers));
      
      setSuccess(`Successfully marked ${response.data.total_completed} travelers as complete`);
      setBulkCompleteDialog(false);
      setSelectedTravelers([]);
      setSelectAll(false);
      fetchTravelers(); // Refresh the list
      fetchStatistics(); // Refresh statistics
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Export handlers
  const handleExportCSV = async () => {
    try {
      setLoading(true);
      
      // Build export parameters from current filters
      const exportParams = {};
      if (selectedTourId) exportParams.tour_id = selectedTourId;
      if (debouncedSearchTerm) exportParams.search = debouncedSearchTerm;
      if (filters.gender) exportParams.gender = filters.gender;
      if (filters.group_number) exportParams.group_number = filters.group_number;
      if (flightFilters.departure_flight) exportParams.departure_flight = flightFilters.departure_flight;
      if (flightFilters.return_flight) exportParams.return_flight = flightFilters.return_flight;
      
      const response = await travelersAPI.exportCSV(exportParams);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `travelers_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      
      const response = await travelersAPI.downloadTemplate();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'travelers_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // PDF Download handlers
  const handlePdfDownload = async () => {
    try {
      // First get available flights for the selected tour
      const flightsResponse = await apiCall(pdfDocumentsAPI.getFlights, selectedTourId);
      setPdfFlights(flightsResponse.departureFlights || flightsResponse.flights || []);
      setPdfReturnFlights(flightsResponse.returnFlights || []);
      setPdfFlightType('departure'); // Reset to departure by default
      setPdfDownloadDialog(true);
    } catch (err) {
      setError('Failed to load flights for PDF download');
    }
  };

  const handlePdfDownloadConfirm = async () => {
    try {
      setLoading(true);

      if (!selectedPdfFlight) {
        setError(`Please select a ${pdfFlightType} flight`);
        return;
      }

      // Build params based on flight type
      const params = {
        tour_id: selectedTourId,
        document_type: pdfDocumentType,
        compile_single: compileSinglePdf,
        watermark_size: watermarkFontSize,
        flight_type: pdfFlightType
      };

      // Add the appropriate flight parameter
      if (pdfFlightType === 'return') {
        params.return_flight = selectedPdfFlight;
      } else {
        params.departure_flight = selectedPdfFlight;
      }

      const response = await pdfDocumentsAPI.compileByFlight(params);

      // Create download link
      const tourData = tours.find(t => t.id === parseInt(selectedTourId));
      const tourName = tourData?.name || `tour_${selectedTourId}`;
      const flightTypePrefix = pdfFlightType === 'return' ? 'return' : 'departure';
      const safeFlightName = selectedPdfFlight === 'ALL_FLIGHTS' ? `All_${flightTypePrefix}_Flights` : selectedPdfFlight.replace(/[^a-zA-Z0-9]/g, '_');
      const fileExtension = compileSinglePdf ? 'pdf' : 'zip';
      const compileSuffix = compileSinglePdf ? '_compiled' : '';
      const filename = `${tourName.replace(/[^a-zA-Z0-9]/g, '_')}_${safeFlightName}_${pdfDocumentType}s${compileSuffix}.${fileExtension}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setPdfDownloadDialog(false);
      setSelectedPdfFlight('');
      setPdfFlightType('departure');
      setCompileSinglePdf(false);

    } catch (err) {
      setError(err.message || 'Failed to compile PDF documents');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (document, title, travelerUuid) => {
    if (!document || !document.id) {
      console.error('No document available to view', { document, title, travelerUuid });
      return;
    }
    
    try {
      // For admin access, use the authenticated endpoint to get blob data
      const response = await documentsAPI.view(document.id);
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      
      // Store the blob URL for cleanup
      setDocumentDialog({
        open: true,
        document,
        title,
        url,
        blobUrl: url, // Store for cleanup
        useIframe: false // Reset iframe flag
      });
    } catch (error) {
      console.error('Error loading document:', error);
      setError(`Failed to load document: ${error.message}`);
    }
  };

  // Mobile card rendering function
  const renderTravelerCard = (traveler, index) => {
    const getStatusColor = (status) => {
      switch(status) {
        case 'Active': return 'success';
        case 'Inactive': return 'default';
        case 'Pending': return 'warning';
        default: return 'default';
      }
    };

    const getDocumentStatusChip = (documents) => {
      const totalRequired = 4; // passport, visa, photo, agreement
      const completed = Object.values(documents || {}).filter(Boolean).length;
      const percentage = (completed / totalRequired) * 100;

      if (percentage === 100) return { label: 'Complete', color: 'success', variant: 'filled' };
      if (percentage > 50) return { label: `${Math.round(percentage)}%`, color: 'warning', variant: 'outlined' };
      return { label: `${Math.round(percentage)}%`, color: 'error', variant: 'outlined' };
    };

    const docStatus = getDocumentStatusChip({
      passport: traveler.passport_document_id,
      visa: traveler.visa_document_id,
      photo: traveler.photo_document_id,
      agreement: traveler.agreement_document_id
    });

    const chips = [
      {
        label: traveler.gender,
        color: traveler.gender === 'Male' ? 'primary' : 'secondary',
        icon: traveler.gender === 'Male' ? <MaleIcon /> : <FemaleIcon />
      },
      {
        label: `Group ${traveler.group_number || 'N/A'}`,
        color: 'default',
        variant: 'outlined'
      },
      docStatus
    ];

    if (traveler.data_complete === false) {
      chips.push({
        label: 'Missing Data',
        color: 'error',
        variant: 'outlined',
        icon: <WarningIcon />
      });
    }

    const details = [
      { label: 'ID Card', value: traveler.id_card || 'N/A' },
      { label: 'Hajji No', value: traveler.hajji_no || 'N/A' },
      { label: 'Type', value: traveler.type || 'N/A' },
      { label: 'MHC No', value: traveler.mhc_no || 'N/A' },
      { label: 'Room', value: traveler.room_no || 'N/A' },
      { label: 'Passport', value: traveler.passport_no || 'N/A' }
    ];

    const actions = [
      {
        label: 'Edit',
        icon: <EditIcon />,
        onClick: (item) => handleEdit(item)
      },
      {
        label: 'QR Code',
        icon: <QrCodeIcon />,
        onClick: (item) => handleDownloadQR(item)
      },
      {
        label: 'Delete',
        icon: <DeleteIcon />,
        onClick: (item) => handleDelete(item)
      }
    ];

    return (
      <MobileDataTable.Card
        key={traveler.id}
        item={traveler}
        primaryText={traveler.name}
        secondaryText={traveler.dhivehi_name || 'No Dhivehi name'}
        avatar={{
          fallback: traveler.name?.charAt(0) || 'T'
        }}
        chips={chips}
        details={details}
        actions={actions}
        onClick={() => {
          // Handle card click if needed
        }}
      />
    );
  };

  return (
    <AdminLayout>
      <ResponsiveContainer>
        {/* Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 0 }}
          mb={3}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Travelers Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => handleRegenerateQRCodes()}
              disabled={!selectedTourId || regeneratingQR}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {regeneratingQR ? 'Starting Job...' : 'Regenerate QR Codes'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialog(true)}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Add Traveler
            </Button>
          </Box>
        </Box>

        {/* Action Bar - Mobile Responsive */}
        <ResponsiveCard
          sx={{ mb: 3 }}
        >
          {/* Top Row - Tour, Search, Sort (exactly like reference) */}
          <Box 
            display="flex" 
            gap={3} 
            alignItems="center" 
            mb={3}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            {/* Tour Selection - Left */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 350px' } }}>
              <FormControl fullWidth>
                <InputLabel>Select Tour</InputLabel>
                <Select
                  value={tours.length > 0 && tours.find(t => t.id.toString() === selectedTourId.toString()) ? selectedTourId : ''}
                  onChange={(e) => handleTourChange(e.target.value)}
                  disabled={toursLoading}
                  label="Select Tour"
                  variant="outlined"
                  sx={{ 
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.400',
                      borderWidth: 1
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select Tour</em>
                  </MenuItem>
                  {tours.map((tour) => (
                    <MenuItem key={tour.id} value={tour.id}>
                      {tour.name} - {tour.destination}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Search - Center */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' } }}>
              <TextField
                ref={searchInputRef}
                fullWidth
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search Travelers"
                disabled={!selectedTourId}
                variant="outlined"
                sx={{ 
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    fontSize: '1rem',
                    '& fieldset': {
                      borderColor: 'grey.400',
                      borderWidth: 1
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Sort - Right */}
            <Box sx={{ flex: '0 0 250px' }}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortConfig.key || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSortConfig({ key: e.target.value, direction: 'asc' });
                    } else {
                      setSortConfig({ key: null, direction: 'asc' });
                    }
                  }}
                  disabled={!selectedTourId}
                  label="Sort By"
                  variant="outlined"
                  sx={{ 
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.400',
                      borderWidth: 1
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Sort By</em>
                  </MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="hajji_no">Hajji No</MenuItem>
                <MenuItem value="bus">Bus No</MenuItem>
                <MenuItem value="group_number">Group</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Bottom Row - Clean organized action buttons */}
          <Box 
            display="flex" 
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={3}
          >
            {/* Primary Actions - Left Side */}
            <Box display="flex" gap={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={handleDownloadTemplate}
                disabled={loading}
                size="small"
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                Template
              </Button>

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={!selectedTourId}
                size="small"
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                Import
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleCsvFileUpload}
                />
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={loading || !selectedTourId}
                size="small"
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                Export
              </Button>

              <Button
                variant="contained"
                startIcon={<QrCodeIcon />}
                onClick={handleBulkQRDownload}
                disabled={loading || !selectedTourId}
                size="small"
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.24)'
                  }
                }}
              >
                QR Codes
              </Button>

              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handlePdfDownload}
                disabled={loading || !selectedTourId}
                size="small"
                sx={{
                  backgroundColor: 'secondary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.24)'
                  }
                }}
              >
                PDF Documents
              </Button>
            </Box>

            {/* Secondary Actions - Right Side */}
            <Box display="flex" gap={1.5} alignItems="center">
              {/* Bulk Operations */}
              {selectedTravelers.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleBulkEdit}
                    disabled={loading}
                    size="small"
                    sx={{
                      backgroundColor: 'success.main',
                      borderRadius: 2,
                      px: 2.5,
                      py: 0.75,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'success.dark',
                        boxShadow: '0 2px 8px rgba(46, 125, 50, 0.24)'
                      }
                    }}
                  >
                    Edit Selected ({selectedTravelers.length})
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={handleBulkComplete}
                    disabled={loading}
                    size="small"
                    sx={{
                      backgroundColor: 'info.main',
                      borderRadius: 2,
                      px: 2.5,
                      py: 0.75,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'info.dark',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.24)'
                      }
                    }}
                  >
                    Mark Complete ({selectedTravelers.length})
                  </Button>
                </>
              )}

              {/* Filter Toggle */}
              <Button
                variant={filters.missing_documents ? "contained" : "outlined"}
                onClick={() => handleFilterChange('missing_documents', !filters.missing_documents)}
                disabled={!selectedTourId}
                size="small"
                sx={{
                  backgroundColor: filters.missing_documents ? 'warning.main' : 'transparent',
                  color: filters.missing_documents ? 'white' : 'warning.main',
                  borderColor: 'warning.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: filters.missing_documents ? 'warning.dark' : 'warning.50',
                    boxShadow: filters.missing_documents ? '0 2px 8px rgba(237, 108, 2, 0.24)' : 'none'
                  }
                }}
              >
                Missing Docs
              </Button>

              {/* Incomplete Data Filter */}
              <Button
                variant={filters.incomplete_data ? "contained" : "outlined"}
                onClick={() => handleFilterChange('incomplete_data', !filters.incomplete_data)}
                disabled={!selectedTourId}
                size="small"
                sx={{
                  backgroundColor: filters.incomplete_data ? 'error.main' : 'transparent',
                  color: filters.incomplete_data ? 'white' : 'error.main',
                  borderColor: 'error.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: filters.incomplete_data ? 'error.dark' : 'error.50',
                    boxShadow: filters.incomplete_data ? '0 2px 8px rgba(211, 47, 47, 0.24)' : 'none'
                  }
                }}
              >
                Missing Essential Data
              </Button>

              {/* Flight Date Filters */}
              {(availableFlightDates.departureDates.length > 0 || availableFlightDates.returnDates.length > 0) && (
                <>
                  {/* Departure Flight Filter */}
                  {availableFlightDates.departureDates.length > 0 && (
                    <FormControl 
                      size="small" 
                      sx={{ 
                        minWidth: 170,
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 9px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          }
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '38px', // Match button height
                        }
                      }}
                    >
                      <InputLabel>Departure Flight</InputLabel>
                      <Select
                        value={flightFilters.departureDate || ''}
                        onChange={(e) => handleFlightFilterChange('departureDate', e.target.value)}
                        label="Departure Flight"
                        disabled={!selectedTourId}
                        sx={{
                          borderRadius: 2,
                          fontSize: '0.875rem',
                          height: '38px',
                          '& .MuiSelect-select': {
                            paddingY: '9px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.main',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.dark',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.main',
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>All Flights</em>
                        </MenuItem>
                        {availableFlightDates.departureDates.map((date) => (
                          <MenuItem key={date} value={date}>
                            {date}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  
                  {/* Return Flight Filter */}
                  {availableFlightDates.returnDates.length > 0 && (
                    <FormControl 
                      size="small" 
                      sx={{ 
                        minWidth: 150,
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 9px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          }
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '38px', // Match button height
                        }
                      }}
                    >
                      <InputLabel>Return Flight</InputLabel>
                      <Select
                        value={flightFilters.returnDate || ''}
                        onChange={(e) => handleFlightFilterChange('returnDate', e.target.value)}
                        label="Return Flight"
                        disabled={!selectedTourId}
                        sx={{
                          borderRadius: 2,
                          fontSize: '0.875rem',
                          height: '38px',
                          '& .MuiSelect-select': {
                            paddingY: '9px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.main',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.dark',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'info.main',
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>All Flights</em>
                        </MenuItem>
                        {availableFlightDates.returnDates.map((date) => (
                          <MenuItem key={date} value={date}>
                            {date}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              {/* View Options */}
              <Button
                variant="outlined"
                onClick={() => setColumnConfigDialog(true)}
                disabled={!selectedTourId}
                size="small"
                sx={{
                  borderColor: 'grey.400',
                  color: 'grey.700',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'grey.50'
                  }
                }}
              >
                Columns
              </Button>
            </Box>
          </Box>
        </ResponsiveCard>


        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        {selectedTourId && (
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Travelers
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.total}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Male
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.male}
                      </Typography>
                    </Box>
                    <MaleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Female
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.female}
                      </Typography>
                    </Box>
                    <FemaleIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Passport Issues
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.passportExpiring}
                      </Typography>
                    </Box>
                    <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Under 12 Years
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.under12}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <Card>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Complete Profiles
                      </Typography>
                      <Typography variant="h4" component="h2">
                        {stats.withAllDocuments}
                      </Typography>
                    </Box>
                    <DocumentIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Travelers Data - Responsive Table/Cards */}
        {!isMobile ? (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 'auto' }}>
              <TableHead 
                sx={{ 
                  backgroundColor: (theme) => 
                    theme.palette.mode === 'dark' 
                      ? theme.palette.background.paper 
                      : theme.palette.grey[50]
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '60px' }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={travelers.length === 0}
                      indeterminate={selectedTravelers.length > 0 && selectedTravelers.length < travelers.length}
                    />
                  </TableCell>
                  {visibleColumns.traveler && (
                    <SortableTableCell sortKey="name">
                      Traveler
                    </SortableTableCell>
                  )}
                  {visibleColumns.tour && <TableCell sx={{ fontWeight: 600 }}>Tour</TableCell>}
                  {visibleColumns.idCard && (
                    <SortableTableCell sortKey="id_card">
                      ID Card
                    </SortableTableCell>
                  )}
                  {visibleColumns.hajjiNo && (
                    <SortableTableCell sortKey="hajji_no">
                      Hajji No
                    </SortableTableCell>
                  )}
                  {visibleColumns.type && (
                    <SortableTableCell sortKey="type">
                      Type
                    </SortableTableCell>
                  )}
                  {visibleColumns.mhcNo && (
                    <SortableTableCell sortKey="mhc_no">
                      MHC No
                    </SortableTableCell>
                  )}
                  {visibleColumns.passport && (
                    <SortableTableCell sortKey="passport_no">
                      Passport
                    </SortableTableCell>
                  )}
                  {visibleColumns.contact && (
                    <SortableTableCell sortKey="phone">
                      Contact
                    </SortableTableCell>
                  )}
                  {visibleColumns.address && (
                    <SortableTableCell sortKey="permanent_atoll">
                      Address
                    </SortableTableCell>
                  )}
                  {visibleColumns.departureFlights && (
                    <SortableTableCell sortKey="departure_flight">
                      Departure Flight
                    </SortableTableCell>
                  )}
                  {visibleColumns.returnFlights && (
                    <SortableTableCell sortKey="return_flight">
                      Return Flight
                    </SortableTableCell>
                  )}
                  {visibleColumns.busNumber && (
                    <SortableTableCell sortKey="bus">
                      Bus Number
                    </SortableTableCell>
                  )}
                  {visibleColumns.documents && <TableCell sx={{ fontWeight: 600 }}>Documents</TableCell>}
                  {visibleColumns.group && (
                    <SortableTableCell sortKey="group_number">
                      Group/Room
                    </SortableTableCell>
                  )}
                  {visibleColumns.medical && <TableCell sx={{ fontWeight: 600 }}>Medical/Agreement</TableCell>}
                  {visibleColumns.status && (
                    <SortableTableCell sortKey="gender">
                      Status
                    </SortableTableCell>
                  )}
                  {visibleColumns.actions && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={Object.values(visibleColumns).filter(Boolean).length} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : travelers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Object.values(visibleColumns).filter(Boolean).length} align="center" sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="body1" color="text.secondary">
                          {!selectedTourId 
                            ? 'Please select a tour to view travelers.'
                            : filters.search || filters.gender || filters.group_number || filters.passport_expiring || filters.missing_documents || filters.incomplete_data
                            ? 'No travelers found. Try adjusting your filters.' 
                            : 'No travelers found for this tour. Add travelers to get started.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedAndFilteredTravelers().map((traveler) => (
                    <TableRow key={traveler.id} hover>
                      <TableCell>
                        <Checkbox
                          checked={selectedTravelers.includes(traveler.id)}
                          onChange={(e) => handleSelectTraveler(traveler.id, e.target.checked)}
                        />
                      </TableCell>
                      {visibleColumns.traveler && (
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {traveler.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {traveler.dhivehi_name || 'No Dhivehi name'}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.tour && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.tour_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {traveler.tour_destination}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.idCard && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {traveler.id_card || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.hajjiNo && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.hajji_no || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.type && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.type || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.mhcNo && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.mhc_no || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.passport && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {traveler.passport_no || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Exp: {formatDate(traveler.passport_expiry)}
                            </Typography>
                            <Box mt={0.5}>
                              {getPassportStatusChip(traveler.passport_status, traveler.passport_expiry)}
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.contact && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {traveler.phone || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Emergency: {traveler.emergency_contact || 'N/A'}
                            </Typography>
                            {traveler.saudi_phone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Saudi: {traveler.saudi_phone}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.address && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {traveler.permanent_atoll && traveler.permanent_island 
                                ? `${traveler.permanent_atoll}. ${traveler.permanent_island}`
                                : 'N/A'
                              }
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Permanent Address
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.departureFlights && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.departure_flight || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.returnFlights && (
                        <TableCell>
                          <Typography variant="body2">
                            {traveler.return_flight || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.busNumber && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {traveler.bus || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.documents && (
                        <TableCell>
                          <Box display="flex" flexWrap="wrap">
                            {getDocumentChips(traveler)}
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.group && (
                        <TableCell>
                          <Typography variant="body2">
                            Group: {traveler.group_number || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Makkah: {traveler.makkah_room || 'N/A'}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Madina: {traveler.madina_room || 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.medical && (
                        <TableCell>
                          <Box>
                            <Chip
                              label={traveler.medical === 'Yes' ? 'Medical' : 'No Medical'}
                              color={traveler.medical === 'Yes' ? 'warning' : 'default'}
                              size="small"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                            <br />
                            <Chip
                              label={traveler.agreement === 'Yes' ? 'Agreement ✓' : 'No Agreement'}
                              color={traveler.agreement === 'Yes' ? 'success' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Chip
                              label={traveler.gender}
                              color={traveler.gender === 'Male' ? 'primary' : 'secondary'}
                              size="small"
                              icon={traveler.gender === 'Male' ? <MaleIcon /> : <FemaleIcon />}
                            />
                            {traveler.data_complete === false && (
                              <Chip
                                label="Missing Essential Data"
                                color="error"
                                size="small"
                                variant="outlined"
                                icon={<WarningIcon />}
                              />
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.actions && (
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, traveler)}
                          >
                            <MoreIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {selectedTourId && pagination.total > 0 && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Showing {currentPage * pagination.limit + 1}-{Math.min((currentPage + 1) * pagination.limit, pagination.total)} of {pagination.total} travelers
                </Typography>
                {pagination.total > pagination.limit && (
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= Math.ceil(pagination.total / pagination.limit) - 1}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          </Paper>
        ) : (
          <Box>
            {/* Mobile Cards View */}
            <MobileDataTable
              data={getSortedAndFilteredTravelers()}
              renderCard={renderTravelerCard}
              loading={loading}
              emptyMessage={!selectedTourId ? "Please select a tour to view travelers" : "No travelers found"}
              loadingMessage="Loading travelers..."
            />

            {/* Mobile Pagination */}
            {selectedTourId && pagination.total > 0 && (
              <ResponsiveCard
                sx={{ mt: 2 }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {currentPage * pagination.limit + 1}-{Math.min((currentPage + 1) * pagination.limit, pagination.total)} of {pagination.total} travelers
                  </Typography>
                  {pagination.total > pagination.limit && (
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        variant="outlined"
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={(currentPage + 1) * pagination.limit >= pagination.total}
                        variant="outlined"
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </Box>
              </ResponsiveCard>
            )}
          </Box>
        )}

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleEdit(selectedTraveler)}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Edit Traveler
          </MenuItem>
          <MenuItem onClick={() => handleDownloadQR(selectedTraveler)}>
            <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Download QR Code
          </MenuItem>
          <MenuItem onClick={() => handleViewProfile(selectedTraveler)}>
            <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
            View Profile
          </MenuItem>
          <MenuItem 
            onClick={() => handleDelete(selectedTraveler)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete Traveler
          </MenuItem>
        </Menu>

        {/* Add Traveler Dialog */}
        <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="md" fullWidth>
          <form onSubmit={handleAddTraveler}>
            <DialogTitle>Add New Traveler</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Tour</InputLabel>
                    <Select
                      name="tour_id"
                      value={tours.find(t => t.id.toString() === formData.tour_id.toString()) ? formData.tour_id : ''}
                      onChange={handleFormChange}
                      label="Tour"
                    >
                      {tours.map((tour) => (
                        <MenuItem key={tour.id} value={tour.id}>
                          {tour.name} - {tour.destination}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hajji Number"
                    name="hajji_no"
                    type="number"
                    value={formData.hajji_no}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Group Number"
                    name="group_number"
                    type="number"
                    value={formData.group_number}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleFormChange}
                      label="Gender"
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Passport Number"
                    name="passport_number"
                    value={formData.passport_number}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Passport Expiry"
                    name="passport_expiry"
                    type="date"
                    value={formData.passport_expiry}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleFormChange}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained">Add Traveler</Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Edit Traveler Dialog */}
        <Dialog open={editDialog} onClose={() => {
          setEditDialog(false);
          setSelectedTraveler(null);
        }} maxWidth="md" fullWidth>
          <form onSubmit={handleUpdateTraveler}>
            <DialogTitle>Edit Traveler</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Dhivehi Name"
                    name="dhivehi_name"
                    value={formData.dhivehi_name}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Arabic Name"
                    name="arabic_name"
                    value={formData.arabic_name}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Type"
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MHC Number"
                    name="mhc_no"
                    value={formData.mhc_no}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ID Card Number"
                    name="id_card"
                    value={formData.id_card}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hajji Number"
                    name="hajji_no"
                    type="number"
                    value={formData.hajji_no}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Group Number"
                    name="group_number"
                    type="number"
                    value={formData.group_number}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleFormChange}
                      label="Gender"
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Passport Number"
                    name="passport_number"
                    value={formData.passport_number}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Passport Expiry"
                    name="passport_expiry"
                    type="date"
                    value={formData.passport_expiry}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Passport Issued Date"
                    name="passport_issued"
                    type="date"
                    value={formData.passport_issued}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Mahram"
                    name="mahram"
                    value={formData.mahram}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Emergency Contact"
                    name="emergency_contact"
                    value={formData.emergency_contact}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SMS Numbers"
                    name="sms_numbers"
                    value={formData.sms_numbers}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Saudi Phone"
                    name="saudi_phone"
                    value={formData.saudi_phone}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Departure Flight"
                    name="departure_flight"
                    value={formData.departure_flight}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Return Flight"
                    name="return_flight"
                    value={formData.return_flight}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bus Number"
                    name="bus"
                    type="number"
                    value={formData.bus}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Care Of"
                    name="care_of"
                    value={formData.care_of}
                    onChange={handleFormChange}
                  />
                </Grid>
                
                {/* Address Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                    Address Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Permanent Address (Dhivehi)"
                    name="permanent_address_dhivehi"
                    value={formData.permanent_address_dhivehi}
                    onChange={handleFormChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Address (Dhivehi)"
                    name="current_address_dhivehi"
                    value={formData.current_address_dhivehi}
                    onChange={handleFormChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Permanent Atoll"
                    name="permanent_atoll"
                    value={formData.permanent_atoll}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Atoll"
                    name="current_atoll"
                    value={formData.current_atoll}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Permanent Island"
                    name="permanent_island"
                    value={formData.permanent_island}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Island"
                    name="current_island"
                    value={formData.current_island}
                    onChange={handleFormChange}
                  />
                </Grid>
                
                {/* Medical & Agreement Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                    Medical & Agreement
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Medical Condition</InputLabel>
                    <Select
                      name="medical"
                      value={formData.medical}
                      onChange={handleFormChange}
                      label="Medical Condition"
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Agreement Signed</InputLabel>
                    <Select
                      name="agreement"
                      value={formData.agreement}
                      onChange={handleFormChange}
                      label="Agreement Signed"
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Room Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                    Room Assignments
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Makkah Room"
                    name="makkah_room"
                    value={formData.makkah_room}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Madina Room"
                    name="madina_room"
                    value={formData.madina_room}
                    onChange={handleFormChange}
                  />
                </Grid>
                
                {/* Document Upload Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                    Upload Documents
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <DocumentUpload 
                    travelerId={formData?.id || null}
                    travelerName={formData?.name || ''}
                    onDocumentChange={(documents) => {
                      // Refresh travelers list to show updated document status
                      // Use setTimeout to avoid potential form submission conflicts
                      setTimeout(() => {
                        fetchTravelers();
                        fetchStatistics();
                      }, 100);
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setEditDialog(false);
                setSelectedTraveler(null);
              }}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={!selectedTraveler?.id}
              >
                Update Traveler
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* CSV Import Dialog */}
        <Dialog open={csvDialog} onClose={() => setCsvDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>CSV Import Preview</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Found {csvData.length} travelers to import. Select a tour and review before confirming.
            </Typography>
            
            {/* Tour Selection */}
            <FormControl fullWidth sx={{ mb: 3 }} required>
              <InputLabel>Select Tour for All Travelers</InputLabel>
              <Select
                value={tours.find(t => t.id.toString() === csvTourId.toString()) ? csvTourId : ''}
                onChange={(e) => setCsvTourId(e.target.value)}
                label="Select Tour for All Travelers"
              >
                {tours.map((tour) => (
                  <MenuItem key={tour.id} value={tour.id}>
                    {tour.name} - {tour.destination}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Passport</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Group</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.slice(0, 10).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.Name || row.name || 'N/A'}</TableCell>
                      <TableCell>{row.Passport || row.passport_no || 'N/A'}</TableCell>
                      <TableCell>{row.Gender || row.gender || 'N/A'}</TableCell>
                      <TableCell>{row.Group || row.group_number || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {csvData.length > 10 && (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  ... and {csvData.length - 10} more travelers
                </Typography>
              )}
            </Box>
            
            {/* Progress Bar */}
            {csvProcessing && (
              <Box sx={{ p: 2, pt: 0 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(csvProgress.current / csvProgress.total) * 100}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" align="center">
                  Processing traveler {csvProgress.current} of {csvProgress.total} (3-second intervals)
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCsvDialog(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleImportTravelers}
              disabled={!csvTourId || csvProcessing}
              startIcon={csvProcessing ? <CircularProgress size={20} /> : null}
            >
              {csvProcessing 
                ? `Importing... ${csvProgress.current}/${csvProgress.total} travelers`
                : 'Import Travelers'
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Column Configuration Dialog */}
        <Dialog open={columnConfigDialog} onClose={() => setColumnConfigDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which columns to display in the travelers table.
            </Typography>
            
            <Box>
              {[
                { key: 'traveler', label: 'Traveler Info', required: true },
                { key: 'tour', label: 'Tour Details' },
                { key: 'idCard', label: 'ID Card Number' },
                { key: 'hajjiNo', label: 'Hajji Number' },
                { key: 'type', label: 'Type' },
                { key: 'mhcNo', label: 'MHC Number' },
                { key: 'passport', label: 'Passport Info' },
                { key: 'contact', label: 'Contact Details' },
                { key: 'address', label: 'Address Info' },
                { key: 'departureFlights', label: 'Departure Flights' },
                { key: 'returnFlights', label: 'Return Flights' },
                { key: 'busNumber', label: 'Bus Number' },
                { key: 'documents', label: 'Documents' },
                { key: 'group', label: 'Group/Room' },
                { key: 'medical', label: 'Medical/Agreement' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: 'Actions', required: true },
              ].map((column) => (
                <Box key={column.key} display="flex" alignItems="center" justifyContent="space-between" py={1}>
                  <Typography variant="body2">
                    {column.label}
                    {column.required && (
                      <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                        (Required)
                      </Typography>
                    )}
                  </Typography>
                  <Button
                    size="small"
                    variant={visibleColumns[column.key] ? "contained" : "outlined"}
                    color={visibleColumns[column.key] ? "primary" : "inherit"}
                    disabled={column.required}
                    onClick={() => {
                      if (!column.required) {
                        setVisibleColumns(prev => ({
                          ...prev,
                          [column.key]: !prev[column.key]
                        }));
                      }
                    }}
                  >
                    {visibleColumns[column.key] ? 'Visible' : 'Hidden'}
                  </Button>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnConfigDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Viewer Dialog */}
        <Dialog
          open={documentDialog.open}
          onClose={() => {
            // Cleanup blob URL when closing
            if (documentDialog.blobUrl) {
              URL.revokeObjectURL(documentDialog.blobUrl);
            }
            setDocumentDialog({ open: false, document: null, title: '', url: '', blobUrl: null, useIframe: false });
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { 
              height: '90vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}>
            <Typography variant="h6" component="div">
              {documentDialog.title}
            </Typography>
            <Chip 
              label={documentDialog.document?.document_type?.replace('_', ' ')?.toUpperCase()} 
              color="primary" 
              size="small"
              variant="outlined"
            />
          </DialogTitle>
          <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            {documentDialog.url ? (
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                backgroundColor: 'background.default',
                minHeight: 0
              }}>
                {documentDialog.document?.file_name?.toLowerCase().endsWith('.pdf') || documentDialog.useIframe ? (
                  // For PDF files or when image fails, use iframe
                  <iframe
                    src={documentDialog.url}
                    width="100%"
                    height="100%"
                    style={{ 
                      border: 'none',
                      minHeight: '70vh'
                    }}
                    title={documentDialog.title}
                    onLoad={() => console.log('Document loaded successfully')}
                    onError={() => console.error('Error loading document in iframe')}
                  />
                ) : (
                  // For images and other files
                  <img
                    src={documentDialog.url}
                    alt={documentDialog.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                    onError={() => {
                      // Fallback to iframe using React state
                      setDocumentDialog(prev => ({ ...prev, useIframe: true }));
                    }}
                    onLoad={() => console.log('Image loaded successfully')}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '70vh',
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress />
                <Typography color="text.secondary">Loading document...</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button 
              onClick={() => window.open(documentDialog.url, '_blank')}
              variant="outlined"
              disabled={!documentDialog.url}
            >
              Open in New Tab
            </Button>
            <Button 
              onClick={() => {
                // Cleanup blob URL when closing
                if (documentDialog.blobUrl) {
                  URL.revokeObjectURL(documentDialog.blobUrl);
                }
                setDocumentDialog({ open: false, document: null, title: '', url: '', blobUrl: null, useIframe: false });
              }}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Edit Dialog */}
        <Dialog open={bulkEditDialog} onClose={() => setBulkEditDialog(false)} maxWidth="md" fullWidth>
          <form onSubmit={handleBulkEditSubmit}>
            <DialogTitle>Bulk Edit {selectedTravelers.length} Travelers</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Only fill in the fields you want to update. Empty fields will be ignored.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Departure Flight"
                    value={bulkEditData.departure_flight}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, departure_flight: e.target.value }))}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Return Flight"
                    value={bulkEditData.return_flight}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, return_flight: e.target.value }))}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Makkah Room"
                    value={bulkEditData.makkah_room}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, makkah_room: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Madina Room"
                    value={bulkEditData.madina_room}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, madina_room: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bus Number"
                    type="number"
                    value={bulkEditData.bus}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, bus: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Group Number"
                    type="number"
                    value={bulkEditData.group_number}
                    onChange={(e) => setBulkEditData(prev => ({ ...prev, group_number: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBulkEditDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Updating...' : `Update ${selectedTravelers.length} Travelers`}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Bulk Complete Confirmation Dialog */}
        <Dialog open={bulkCompleteDialog} onClose={() => setBulkCompleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Bulk Completion</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to mark {selectedTravelers.length} selected travelers as having complete data?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>Mark all selected travelers as data complete</li>
              <li>Clear any missing fields flags</li>
              <li>Remove them from incomplete data filters</li>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkCompleteDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkCompleteSubmit} 
              variant="contained" 
              color="info"
              disabled={loading}
            >
              {loading ? 'Processing...' : `Mark ${selectedTravelers.length} Complete`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* PDF Download Dialog */}
        <Dialog
          open={pdfDownloadDialog}
          onClose={() => {
            setPdfDownloadDialog(false);
            setSelectedPdfFlight('');
            setPdfFlightType('departure');
            setCompileSinglePdf(false);
            setWatermarkFontSize(42);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Download PDF Documents by Flight</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={pdfDocumentType}
                  onChange={(e) => setPdfDocumentType(e.target.value)}
                  label="Document Type"
                >
                  <MenuItem value="visa">Visa Documents</MenuItem>
                  <MenuItem value="passport">Passport Copies</MenuItem>
                </Select>
              </FormControl>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Flight Type</Typography>
                <ToggleButtonGroup
                  value={pdfFlightType}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setPdfFlightType(newValue);
                      setSelectedPdfFlight(''); // Reset flight selection when type changes
                    }
                  }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="departure">
                    <DepartureIcon sx={{ mr: 1 }} />
                    Departure
                  </ToggleButton>
                  <ToggleButton value="return">
                    <ArrivalIcon sx={{ mr: 1 }} />
                    Arrival/Return
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{pdfFlightType === 'return' ? 'Return Flight' : 'Departure Flight'}</InputLabel>
                <Select
                  value={selectedPdfFlight}
                  onChange={(e) => setSelectedPdfFlight(e.target.value)}
                  label={pdfFlightType === 'return' ? 'Return Flight' : 'Departure Flight'}
                  disabled={(pdfFlightType === 'return' ? pdfReturnFlights : pdfFlights).length === 0}
                >
                  <MenuItem value="">
                    <em>Select Flight</em>
                  </MenuItem>
                  <MenuItem value="ALL_FLIGHTS">
                    <strong>🌍 All {pdfFlightType === 'return' ? 'Return' : 'Departure'} Dates</strong>
                  </MenuItem>
                  {(pdfFlightType === 'return' ? pdfReturnFlights : pdfFlights).map((flight) => (
                    <MenuItem key={flight} value={flight}>
                      {flight}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Checkbox
                  checked={compileSinglePdf}
                  onChange={(e) => setCompileSinglePdf(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">
                  Compile into single PDF document with watermarks
                </Typography>
              </Box>

              <TextField
                label="Watermark Font Size"
                type="number"
                value={watermarkFontSize}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 42;
                  setWatermarkFontSize(Math.max(10, Math.min(100, value)));
                }}
                fullWidth
                sx={{ mb: 2 }}
                helperText="Font size for watermarks (10-100 pixels)"
                InputProps={{
                  inputProps: { min: 10, max: 100, step: 1 }
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {compileSinglePdf
                  ? '✓ All documents will be combined into one PDF file with Hajji No and Name watermarks on each page'
                  : '✓ Documents will be provided as individual watermarked files in a ZIP archive'
                }
              </Typography>
              
              {(pdfFlightType === 'return' ? pdfReturnFlights : pdfFlights).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No {pdfFlightType === 'return' ? 'return' : 'departure'} flights available for this tour
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setPdfDownloadDialog(false);
              setSelectedPdfFlight('');
              setPdfFlightType('departure');
              setCompileSinglePdf(false);
              setWatermarkFontSize(42);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handlePdfDownloadConfirm}
              variant="contained"
              disabled={!selectedPdfFlight || loading}
            >
              {loading ? 'Compiling...' : 'Download PDF Documents'}
            </Button>
          </DialogActions>
        </Dialog>

      </ResponsiveContainer>
    </AdminLayout>
  );
};

export default TravelersManagement;